import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ViewStyle,
  StyleProp,
} from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import { CommunityResource } from '@/types/residents/communityResource';
import { RESOURCES_COLORS } from '@/constants/residents/mobile/resourcesColors';
import { ResourceIcon } from '@/components/residents/resources/ResourceIcon';
import { MOBILE_TYPOGRAPHY } from '@/constants/residents/mobile/mobileTypography';

interface MobileResourceCardProps {
  /** Resource data (optional if using custom title/description) */
  resource?: CommunityResource;
  /** Custom title (overrides resource.name) */
  title?: string;
  /** Custom description (overrides resource.description) */
  description?: string;
  /** Custom icon component (overrides ResourceIcon) */
  icon?: React.ReactNode;
  /** Icon name for Ionicons (alternative to icon component) */
  iconName?: string;
  /** Press handler - receives resource if provided */
  onPress: (resource?: CommunityResource) => void;
  /** Link text (default: "See details") */
  linkText?: string;
  /** Custom style for container */
  style?: StyleProp<ViewStyle>;
  /** Max lines for description (default: 2) */
  descriptionLines?: number;
}

/**
 * Mobile Resource Card
 * 
 * Flexible card component with icon avatar, title, description, and action link.
 * Can be used with CommunityResource data or custom props.
 * Matches Figma design for My Resources screen.
 * 
 * @example
 * // With resource data
 * <MobileResourceCard
 *   resource={resource}
 *   onPress={(r) => navigation.navigate('Detail', { resource: r })}
 * />
 * 
 * @example
 * // With custom props
 * <MobileResourceCard
 *   title="Contact the office"
 *   description="Get in touch with your property management"
 *   iconName="chatbubble-outline"
 *   onPress={() => navigation.navigate('ContactOffice')}
 * />
 */
export const MobileResourceCard: React.FC<MobileResourceCardProps> = ({
  resource,
  title: customTitle,
  description: customDescription,
  icon: customIcon,
  iconName,
  onPress,
  linkText = 'See details',
  style,
  descriptionLines = 2,
}) => {
  const title = customTitle || resource?.name || resource?.title || '';
  const description = customDescription || resource?.description || '';

  const handlePress = () => {
    onPress(resource);
  };

  const renderIcon = () => {
    if (customIcon) {
      return customIcon;
    }
    if (iconName) {
      return <Icon name={iconName} size={24} color={RESOURCES_COLORS.textPrimary} />;
    }
    if (resource) {
      return (
        <ResourceIcon
          iconKey={resource.icon || resource.iconName}
          size={24}
          color={RESOURCES_COLORS.textPrimary}
        />
      );
    }
    return null;
  };

  return (
    <TouchableOpacity
      style={[styles.container, style]}
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={`${title}. ${description}. ${linkText}`}
    >
      {/* Avatar with Icon */}
      <View style={styles.avatar}>
        {renderIcon()}
      </View>

      {/* Content */}
      <View style={styles.content}>
        {/* Text Section */}
        <View style={styles.textSection}>
          <Text style={styles.title} numberOfLines={1}>
            {title}
          </Text>
          <Text style={styles.description} numberOfLines={descriptionLines}>
            {description}
          </Text>
        </View>

        {/* See Details Link */}
        <View style={styles.linkRow}>
          <Text style={styles.linkText}>{linkText}</Text>
          <Icon name="chevron-forward" size={20} color={RESOURCES_COLORS.linkBlue} />
        </View>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: RESOURCES_COLORS.cardBackground,
    borderRadius: 16,
    padding: 20,
    flexDirection: 'row',
    gap: 16,
  },
  avatar: {
    width: 68,
    height: 68,
    borderRadius: 34,
    backgroundColor: RESOURCES_COLORS.avatarBackground,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
    gap: 12,
  },
  textSection: {
    gap: 2,
  },
  title: {
    ...MOBILE_TYPOGRAPHY.title,
    color: RESOURCES_COLORS.textPrimary,
  },
  description: {
    ...MOBILE_TYPOGRAPHY.body,
    color: RESOURCES_COLORS.textSecondary,
  },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  linkText: {
    ...MOBILE_TYPOGRAPHY.link,
    color: RESOURCES_COLORS.linkBlue,
  },
});
